import { useQuery } from 'react-query'

import { primaryCategoryRepository } from '@webapp/core/data-access/repositories/PrimaryCategoryRepository'
import { PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { usePagination } from '@webapp/core/hooks/usePagination'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { primaryCategoryQueryConfig } from '@webapp/platform/iam/query-configs/primaryCategoryQueryConfig'

type UseQueryPrimaryCategoriesParams = NonNullable<
  Parameters<typeof primaryCategoryRepository.list>[0]
>

type UseQueryPrimaryCategoriesReturn = {
  primaryCategories: PrimaryCategory[]
  pagination?: RepoPaginationResponse
  isFetching: boolean
  resetPagination: () => void
}

export const useQueryPrimaryCategories = ({
  params,
  onError,
}: {
  params?: UseQueryPrimaryCategoriesParams
  onError?: () => void
}): UseQueryPrimaryCategoriesReturn => {
  const {
    params: paginationParams,
    updateNumPages,
    reset,
  } = usePagination({
    id: 'primary-category-list',
  })

  const { data, isFetching } = useQuery({
    queryKey: primaryCategoryQueryConfig.list({ paginationParams, params })
      .queryKey,
    queryFn: () =>
      primaryCategoryRepository.list({ ...paginationParams, ...params }),
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
    onError,
  })

  const primaryCategories = data?.data || []

  return {
    primaryCategories: primaryCategories.map(
      (primaryCategory) => new PrimaryCategory(primaryCategory)
    ),
    pagination: data?.pagination,
    isFetching,
    resetPagination: reset,
  }
}
